import { elementBounds, elementCentre } from './svgElementGeometry';

/**
 * Placement metadata parsed from `#hardware-guides`.
 * The slab SVG says where hardware may go. It does not say which combinations
 * are valid — that belongs in the hardware compatibility layer.
 */
export interface HardwareAnchor {
  id: string;
  role: string;
  anchorType: string;
  position: string;
  x: number;
  y: number;
}

export interface HardwareZone {
  id: string;
  role: string;
  zoneType: string;
  position: string;
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface HardwareGuides {
  anchors: HardwareAnchor[];
  zones: HardwareZone[];
}

export const EMPTY_HARDWARE_GUIDES: HardwareGuides = {
  anchors: [],
  zones: [],
};

function hasClass(element: Element, className: string): boolean {
  return (element.getAttribute('class') ?? '')
    .split(/\s+/)
    .includes(className);
}

function hardwareGroup(root: Element): Element | null {
  return root.querySelector('#hardware-guides, [id="hardware-guides"]');
}

function inkscapeLabel(element: Element): string {
  return (
    element.getAttribute('inkscape:label') ??
    element.getAttributeNS('http://www.inkscape.org/namespaces/inkscape', 'label') ??
    element.getAttribute('data-label') ??
    ''
  );
}

/** Product SVG attachment origin. Maps onto whichever slab guide the product is placed on. */
export const PRODUCT_ATTACHMENT_ANCHOR_ID = 'anchor-point';

const PRODUCT_ANCHOR_IDS = new Set([
  PRODUCT_ATTACHMENT_ANCHOR_ID,
  'standard-anchor',
  'heritage-anchor',
  'pullbar-anchor',
  'letterplate-anchor',
  'knocker-anchor',
  'spyhole-anchor',
]);

function normalizeAnchorName(value: string): string {
  return value.trim().toLowerCase().replace(/[\s_]+/g, '-');
}

function productAnchorId(element: Element): string | null {
  const candidates = [element.getAttribute('id') ?? '', inkscapeLabel(element)].map(normalizeAnchorName);
  return candidates.find((candidate) => PRODUCT_ANCHOR_IDS.has(candidate)) ?? null;
}

function productAnchorCentre(element: Element, root: Element): { x: number; y: number } | null {
  const tag = element.tagName.toLowerCase();
  if (tag === 'g' || tag === 'svg') {
    const marker = element.querySelector('circle, ellipse');
    if (marker) return elementCentre(marker, root);
  }
  return elementCentre(element, root);
}

function hardwareElementId(element: Element): string {
  const id = element.getAttribute('id') ?? '';
  const label = inkscapeLabel(element);
  if (label && /^circle\d+$/i.test(id)) return label;
  return id || label;
}

function hardwareElementRole(element: Element, id: string): string {
  if (id === 'knocker-anchor') return 'knocker';
  if (id === 'spyhole-anchor') return 'spyhole';
  return element.getAttribute('data-hardware-role') ?? '';
}

function parseAnchor(element: Element, root: Element): HardwareAnchor | null {
  const centre = elementCentre(element, root);
  if (!centre) return null;
  const id = hardwareElementId(element);
  return {
    id,
    role: hardwareElementRole(element, id),
    anchorType: element.getAttribute('data-anchor-type') ?? '',
    position: element.getAttribute('data-position') ?? '',
    x: centre.x,
    y: centre.y,
  };
}

function parseZone(element: Element, root: Element): HardwareZone | null {
  const bounds = elementBounds(element, root);
  if (!bounds || bounds.width <= 0 || bounds.height <= 0) return null;
  return {
    id: hardwareElementId(element),
    role: element.getAttribute('data-hardware-role') ?? '',
    zoneType: element.getAttribute('data-zone-type') ?? '',
    position: element.getAttribute('data-position') ?? '',
    x: bounds.x,
    y: bounds.y,
    width: bounds.width,
    height: bounds.height,
  };
}

/**
 * Read machine-readable hardware anchors and zones from `#hardware-guides`.
 * Absence of the group or of a given role means placement is not declared.
 */
export function extractHardwareGuides(root: Element): HardwareGuides {
  const group = hardwareGroup(root);
  if (!group) return { anchors: [], zones: [] };

  const anchors: HardwareAnchor[] = [];
  const zones: HardwareZone[] = [];

  Array.from(group.querySelectorAll('*')).forEach((element) => {
    if (hasClass(element, 'hardware-anchor')) {
      const anchor = parseAnchor(element, root);
      if (anchor) anchors.push(anchor);
      return;
    }
    if (hasClass(element, 'hardware-zone')) {
      const zone = parseZone(element, root);
      if (zone) zones.push(zone);
    }
  });

  return { anchors, zones };
}

function productAnchorType(element: Element, id: string): string {
  const authored = element.getAttribute('data-anchor-type');
  if (authored) return authored;
  if (id === PRODUCT_ATTACHMENT_ANCHOR_ID) return 'attachment';
  if (id === 'standard-anchor') return 'spindle-centre';
  return 'centre';
}

function productAnchorPosition(element: Element, id: string): string {
  const authored = element.getAttribute('data-position');
  if (authored) return authored;
  return id === 'standard-anchor' ? 'standard' : '';
}

/**
 * Product SVGs declare an attachment origin via `#hardware-guides`, or as an
 * Inkscape circle/ellipse labelled `anchor-point` / `anchor point`. Older files
 * may still use slab-named labels such as `standard-anchor`.
 */
export function extractProductHardwareGuides(root: Element): HardwareGuides {
  const fromContract = extractHardwareGuides(root);
  const anchors = [...fromContract.anchors];
  const seen = new Set(anchors.map((anchor) => anchor.id));

  Array.from(root.querySelectorAll('*')).forEach((element) => {
    const id = productAnchorId(element);
    if (!id || seen.has(id)) return;
    const centre = productAnchorCentre(element, root);
    if (!centre) return;
    seen.add(id);
    anchors.push({
      id,
      role: hardwareElementRole(element, id),
      anchorType: productAnchorType(element, id),
      position: productAnchorPosition(element, id),
      x: centre.x,
      y: centre.y,
    });
  });

  return { anchors, zones: fromContract.zones };
}

export function stripProductHardwareMetadata(root: Element): void {
  root.querySelectorAll('#hardware-guides, [id="hardware-guides"]').forEach((node) => node.remove());
  Array.from(root.querySelectorAll('*'))
    .filter((element) => productAnchorId(element))
    .forEach((element) => element.remove());
}

export function findHardwareAnchor(
  guides: HardwareGuides,
  id: string
): HardwareAnchor | undefined {
  return guides.anchors.find((anchor) => anchor.id === id);
}

export function findHardwareZone(
  guides: HardwareGuides,
  id: string
): HardwareZone | undefined {
  return guides.zones.find((zone) => zone.id === id);
}

/**
 * Placement options declared by the currently selected slab.
 * Absence means that placement is not offered for this style.
 */
export interface HardwarePlacementAvailability {
  standardHandle: boolean;
  heritage: boolean;
  pullBar: boolean;
  knocker: boolean;
  spyhole: boolean;
  letterplatePositions: HardwareZone[];
}

export function hardwarePlacementAvailability(
  guides: HardwareGuides
): HardwarePlacementAvailability {
  const letterplatePositions = guides.zones.filter(
    (zone) => zone.role === 'letterplate' || zone.id.startsWith('letterplate-position-')
  );
  return {
    standardHandle: Boolean(findHardwareAnchor(guides, 'standard-anchor')),
    heritage: Boolean(findHardwareAnchor(guides, 'heritage-anchor')),
    pullBar: Boolean(findHardwareAnchor(guides, 'pullbar-anchor')),
    knocker: Boolean(findHardwareAnchor(guides, 'knocker-anchor')),
    spyhole: Boolean(findHardwareAnchor(guides, 'spyhole-anchor')),
    letterplatePositions,
  };
}
